'use client';

import { BenefitsSection } from '@/components/sections/benefits-section';
import { CustomerVideos } from '@/components/sections/customer-videos';
import { FaqSection } from '@/components/sections/faq-section';
import {
  BubbleQuickReplies,
  BubbleTimeSlots,
  SubpageHero,
} from '@/components/sections/subpage-hero';
import Testimonials from '@/components/sections/testimonials';
import { useTranslation } from '@/lib/i18n/context';

const CONTENT = {
  en: {
    eyebrow: 'Channels',
    title: 'Chat that actually resolves',
    subtitleA: 'Your customers get instant answers — not bot replies.',
    subtitleB: 'Your team handles the hard cases, not the queue.',
    b1: 'Can I move my callback to tomorrow morning?',
    b2: 'Of course — pick a slot that works:',
    slotsDay: 'May 13',
    confirmLabel: 'Confirming…',
    b3: 'Also, can I get a refund on order #4521?',
    b4: 'Sure. What should we do?',
    optFullRefund: 'Full refund',
    optStoreCredit: 'Store credit',
    optReplaceItem: 'Replace item',
  },
  ar: {
    eyebrow: 'القنوات',
    title: 'دردشة تحل المشكلة فعلاً',
    subtitleA: 'عميلك ياخذ رد فوري — لا رد روبوت.',
    subtitleB: 'وفريقك يركّز على الحالات الصعبة، لا على الطابور.',
    b1: 'أقدر أنقل مكالمتي لبكرة الصبح؟',
    b2: 'أكيد — اختر الوقت اللي يناسبك:',
    slotsDay: '١٣ مايو',
    confirmLabel: 'جارٍ التأكيد…',
    // ⁦…⁩ = LRI/PDI. Without the isolate RTL bidi renders "#4521" as "4521#".
    b3: 'وسؤال ثاني — أقدر أسترجع فلوس الطلب ⁦#4521⁩؟',
    b4: 'أكيد. وش تفضّل؟',
    optFullRefund: 'استرجاع كامل',
    optStoreCredit: 'رصيد بالمتجر',
    optReplaceItem: 'استبدال المنتج',
  },
} as const;

export default function WebChatPage() {
  const { t, locale } = useTranslation();
  const c = locale === 'ar' ? CONTENT.ar : CONTENT.en;

  return (
    <>
      <SubpageHero
        eyebrow={c.eyebrow}
        title={c.title}
        subtitle={
          <>
            {c.subtitleA}
            <br />
            {c.subtitleB}
          </>
        }
        videoSrc="/home/features/chat.mp4"
        // English unchanged; on /ar fall through to SubpageHero's localized
        // default ("الوكيل الذكي") instead of the untranslated "Open Agent".
        bubbleAgentName={locale === 'ar' ? undefined : 'Open Agent'}
        bubbles={[
          { role: 'user', text: c.b1 },
          {
            role: 'agent',
            text: c.b2,
            widget: (
              <BubbleTimeSlots
                day={c.slotsDay}
                slots={['08:00', '08:30', '09:00', '09:30', '10:00']}
                selected="08:00"
                confirmLabel={c.confirmLabel}
              />
            ),
          },
          { role: 'user', text: c.b3 },
          {
            role: 'agent',
            text: c.b4,
            widget: (
              <BubbleQuickReplies
                options={[c.optFullRefund, c.optStoreCredit, c.optReplaceItem]}
                selected={c.optFullRefund}
              />
            ),
          },
        ]}
      />

      <CustomerVideos />

      <BenefitsSection
        title={t.channels['Web Chat Features']}
        subtitle={t.channels['Real-time AI conversations on your website']}
        learnMoreLink="#faq"
        accentColor="emerald"
        theme="light"
        benefits={[
          {
            title: t.channels['Embeddable Widget'],
            description:
              t.channels[
                'Add the chat widget with one line of code. Fully customizable to match your brand.'
              ],
            linkText: '',
            linkHref: '#',
            imageUrl:
              'https://deifkwefumgah.cloudfront.net/shadcnblocks/block/placeholder-1.svg',
            imagePosition: 'right',
          },
          {
            title: t.channels['AI Vision & Memory'],
            description:
              t.channels[
                'Agent 5 can see and understand images shared in chat. Remembers entire conversation history for context-aware responses.'
              ],
            linkText: '',
            linkHref: '#',
            imageUrl:
              'https://deifkwefumgah.cloudfront.net/shadcnblocks/block/placeholder-2.svg',
            imagePosition: 'left',
          },
          {
            title: t.channels['Handles Complex Requests'],
            description:
              t.channels[
                'More than simple FAQs. Agent 5 processes returns, account updates, and multi-step workflows.'
              ],
            linkText: '',
            linkHref: '#',
            imageUrl:
              'https://deifkwefumgah.cloudfront.net/shadcnblocks/block/placeholder-3.svg',
            imagePosition: 'right',
          },
          {
            title: t.channels['Rich Media Support'],
            description:
              t.channels[
                'Share images, videos, files, and links. Agent 5 sends visual guides and interactive elements.'
              ],
            linkText: '',
            linkHref: '#',
            imageUrl:
              'https://deifkwefumgah.cloudfront.net/shadcnblocks/block/placeholder-4.svg',
            imagePosition: 'left',
          },
          {
            title: t.channels['Interactive UI Elements'],
            description:
              t.channels[
                'Render buttons, forms, cards, and carousels in responses. Not just text.'
              ],
            linkText: '',
            linkHref: '#',
            imageUrl:
              'https://deifkwefumgah.cloudfront.net/shadcnblocks/block/placeholder-5.svg',
            imagePosition: 'right',
          },
          {
            title: t.channels['User Authentication & Personalization'],
            description:
              t.channels[
                'Identify users and pass custom data. AI uses customer context for personalized responses.'
              ],
            linkText: '',
            linkHref: '#',
            imageUrl:
              'https://deifkwefumgah.cloudfront.net/shadcnblocks/block/placeholder-1.svg',
            imagePosition: 'left',
          },
          {
            title: t.channels['Human + AI Collaboration'],
            description:
              t.channels[
                'Seamless handoff between AI and human agents. Smooth transitions invisible to customers.'
              ],
            linkText: '',
            linkHref: '#',
            imageUrl:
              'https://deifkwefumgah.cloudfront.net/shadcnblocks/block/placeholder-2.svg',
            imagePosition: 'right',
          },
          {
            title: t.channels['Proactive Chat'],
            description:
              t.channels[
                'Trigger messages based on user behavior. Offer help when customers need it most.'
              ],
            linkText: '',
            linkHref: '#',
            imageUrl:
              'https://deifkwefumgah.cloudfront.net/shadcnblocks/block/placeholder-3.svg',
            imagePosition: 'left',
          },
        ]}
      />

      {/* FAQ Section */}
      <FaqSection
        title={t.channels['Frequently asked questions']}
        subtitle={
          t.channels['Find answers to common questions about Web Chat.']
        }
        items={[
          {
            question: t.channels['How do I add the chat widget to my website?'],
            answer:
              t.channels[
                'Copy one line of code and paste it before the closing body tag on your website. The widget appears automatically.'
              ],
          },
          {
            question: t.channels['Can I customize the widget appearance?'],
            answer:
              t.channels[
                'Yes. Change colors, position, avatar, greeting message, and behavior through the dashboard. No coding required.'
              ],
          },
          {
            question: t.channels['Does it work on mobile devices?'],
            answer:
              t.channels[
                'Yes. The chat widget is fully responsive and works on all devices—desktop, tablet, and mobile.'
              ],
          },
          {
            question: t.channels['Can customers attach files in the chat?'],
            answer:
              t.channels[
                'Yes. Customers can upload images, PDFs, and other files directly in the chat. Agent 5 can view and reference them.'
              ],
          },
          {
            question: t.channels['How does handoff to human agents work?'],
            answer:
              t.channels[
                'Agent 5 transfers the conversation to a human agent with full chat history. The customer stays in the same chat window.'
              ],
          },
          {
            question: t.channels['Can I trigger proactive messages?'],
            answer:
              t.channels[
                'Yes. Set up triggers based on time on page, scroll depth, exit intent, or custom events to engage visitors at the right moment.'
              ],
          },
        ]}
      />

      <Testimonials />
    </>
  );
}
